// src/pages/common/reports/R0006/services/salesPerformanceService.js

import { API } from 'aws-amplify';
import { getR0003Reportv2 } from 'graphql/queries';
import { fetchMetricDataByClientDateAndName } from 'services/metricData/fetchMetricDataByClientDateAndName';
import { format, startOfWeek, addDays, parseISO, isWithinInterval } from 'date-fns';
import {
  getAvgCheckHighThreshold,
  applyAvgCheckAdjustment
} from 'services/thresholds/thresholdService';
import { getTheoHoursConfig } from 'clients/grm/views/R0003/Report/utils/mergeMetrics/constants';

/**
 * Fetch and process Sales Performance data for R0006
 * Combines R0003 projection data with MetricData actual sales
 *
 * @param {Object} dateRange - Date range for the report
 * @param {string} selectedArea - Selected area filter
 * @param {Object} user - User object
 * @param {string} role - User role
 * @param {Map} storeIdMap - Map of store names to IDs
 * @param {Array} areasData - Areas data including thresholds
 * @param {Array} thresholds - Global threshold configuration (avgCheck thresholds)
 */
export async function fetchSalesPerformanceData(dateRange, selectedArea, user, role, storeIdMap, areasData, thresholds) {
  console.log('💰 FETCHING SALES PERFORMANCE DATA');

  try {
    // Validate inputs
    if (!dateRange || !dateRange.startDate) {
      throw new Error('dateRange and dateRange.startDate are required');
    }
    if (!user) {
      throw new Error('user parameter is required');
    }

    // Fetch projection data from R0003 for all weeks in date range
    const projectionData = await fetchProjectionData(dateRange, user, role);
    if (!projectionData || projectionData.length === 0) {
      console.error('🚨 NO PROJECTION DATA RECEIVED');
      throw new Error('No sales projection data available for the selected date range');
    }
    
    // Fetch actual sales and check count from MetricData
    const actualData = await fetchActualData(dateRange, selectedArea);
    if (!actualData || actualData.length === 0) {
      console.error('🚨 NO ACTUAL SALES DATA RECEIVED');
      throw new Error('No actual sales data available for the selected date range');
    }
    
    // Process and combine the data
    const salesPerformanceData = await processSalesPerformanceData(
      projectionData,
      actualData,
      selectedArea,
      storeIdMap,
      dateRange,
      areasData,
      thresholds
    );
    
    if (!salesPerformanceData || !salesPerformanceData.restaurants || salesPerformanceData.restaurants.length === 0) {
      console.error('🚨 NO RESTAURANTS DATA AFTER PROCESSING');
      throw new Error('Failed to process sales data - no restaurants data generated');
    }
    
    
    // Attach R0003 data for reuse by labor hours service
    salesPerformanceData.__r0003Data = projectionData;
    
    return salesPerformanceData;
    
  } catch (error) {
    console.error('🚨 SALES PERFORMANCE ERROR:', error.message);
    throw error;
  }
}

/**
 * Get week start date (Monday) that contains the selected date range
 * R0003 data is stored by week, so we need the Monday of the week containing our date range
 */
function getWeekStart(dateString) {
  const date = parseISO(dateString);
  const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Monday = 1
  return format(weekStart, 'yyyy-MM-dd');
}

/**
 * Get all week starts that fall within the date range
 */
function getWeekStartsInRange(startDate, endDate) {
  const weekStarts = [];
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  
  // Find the Monday of the week containing startDate
  let currentWeekStart = startOfWeek(start, { weekStartsOn: 1 });
  
  // Collect all Mondays until we pass the endDate
  while (currentWeekStart <= end) {
    weekStarts.push(format(currentWeekStart, 'yyyy-MM-dd'));
    currentWeekStart = addDays(currentWeekStart, 7);
  }
  
  return weekStarts;
}

/**
 * Fetch projection data from R0003 MongoDB source for all weeks in date range
 */
async function fetchProjectionData(dateRange, user, role) {
  try {
    // Get all week starts that need to be fetched
    const weekStarts = getWeekStartsInRange(dateRange.startDate, dateRange.endDate);
    
    // Fetch data for each week in parallel
    console.log(`📡 Fetching R0003 data for ${weekStarts.length} weeks...`);
    const weekPromises = weekStarts.map(weekStart => 
      fetchSingleWeekProjectionData(weekStart, user, role)
    );
    
    const allWeeksData = await Promise.all(weekPromises);
    
    // Merge data from all weeks
    const mergedProjectionData = mergeMultiWeekProjectionData(allWeeksData);
    
    
    return mergedProjectionData;
  } catch (error) {
    console.error('❌ Error fetching multi-week projection data:', error);
    throw error;
  }
}

/**
 * Fetch projection data for a single week
 */
async function fetchSingleWeekProjectionData(weekStart, user, role) {
  try {
    const variables = { week: weekStart, user: user?.userName || user, role };
    
    const response = await API.graphql({
      query: getR0003Reportv2,
      variables,
      authMode: 'AMAZON_COGNITO_USER_POOLS'
    });
    
    if (!response?.data?.getR0003Reportv2?.data) {
      console.warn(`⚠️ No data for week ${weekStart}`);
      return { weekStart, data: [] };
    }
    
    return { 
      weekStart, 
      data: response.data.getR0003Reportv2.data 
    };
  } catch (error) {
    console.error(`❌ Error fetching week ${weekStart}:`, error);
    return { weekStart, data: [] };
  }
}

/**
 * Merge projection data from multiple weeks
 * Note: Adjusted theoretical hours will be calculated later when we have access to MetricData
 */
function mergeMultiWeekProjectionData(allWeeksData) {
  // Group by area name
  const areaMap = new Map();
  
  allWeeksData.forEach(({ weekStart, data }) => {
    data.forEach(areaData => {
      const areaName = areaData.area;
      
      if (!areaMap.has(areaName)) {
        areaMap.set(areaName, {
          area: areaName,
          weekDataMap: new Map()
        });
      }
      
      // Store the raw data - we'll calculate adjusted hours later when we have MetricData
      areaMap.get(areaName).weekDataMap.set(weekStart, areaData.data);
    });
  });
  
  // Convert back to array format with all weeks' data
  return Array.from(areaMap.values());
}

/**
 * Calculate adjusted theoretical hours for all areas using MetricData actual sales
 */
function calculateAdjustedTheoreticalHoursWithMetricData(projectionData, actualDataByStore, storeIdMap, areasData) {
  console.log('🧮 Starting adjusted theoretical hours calculation...');
  
  return projectionData.map(areaData => {
    
    const enrichedArea = {
      ...areaData,
      weekDataMap: new Map()
    };
    
    // Process each week's data
    for (const [weekStart, sectionData] of areaData.weekDataMap) {
      
      const enrichedSectionData = calculateAdjustedHoursForWeek(
        sectionData, 
        weekStart, 
        actualDataByStore, 
        storeIdMap,
        areasData
      );
      
      enrichedArea.weekDataMap.set(weekStart, enrichedSectionData);
    }
    
    return enrichedArea;
  });
}

/**
 * Calculate adjusted theoretical hours for a single week using MetricData actual sales
 */
function calculateAdjustedHoursForWeek(sectionData, weekStartDate, actualDataByStore, storeIdMap, areasData) {
  const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const WEEKDAY_DAYS = ['Mon', 'Tue', 'Wed', 'Thu'];

  // Create a map of day names to dates for the week
  const weekDates = DAYS_OF_WEEK.reduce((acc, day, index) => {
    const date = new Date(weekStartDate);
    date.setDate(date.getDate() + index);
    acc[day] = date.toISOString().split('T')[0]; // 'YYYY-MM-DD' format
    return acc;
  }, {});

  const NUM_DATA_ROWS = 11; // Each store takes 11 rows in R0003 structure
  
  // Create a copy of section data to avoid mutating original
  const enrichedSectionData = [...sectionData];
  
  // Process each store section
  for (let i = 0; i < enrichedSectionData.length - NUM_DATA_ROWS; i += NUM_DATA_ROWS) {
    const storeNameRow = enrichedSectionData[i];
    if (!storeNameRow || !storeNameRow.day) continue;
    
    const storeName = storeNameRow.day;
    
    // Skip if this is not a store (e.g., "Area Rollup")
    if (storeName.startsWith('Area Rollup')) {
      break;
    }
    
    
    // Get store ID for MetricData lookup
    const storeId = getStoreIdFromMap(storeName, storeIdMap);
    const storeActualData = storeId ? actualDataByStore[storeId] : null;
    
    // Get base theoretical hours from areasData
    const baseTheoHours = getBaseTheoreticalHours(storeName, areasData);
    
    
    // Initialize weekly totals for adjusted hours
    const weekTotals = {
      adjTheoFOH: 0,
      adjTheoBOH: 0
    };

    // Process each day's data for the store (rows i+1 through i+7)
    DAYS_OF_WEEK.forEach((day, dayIndex) => {
      const rowIndex = i + dayIndex + 1; // +1 because days start after store name
      if (rowIndex >= enrichedSectionData.length) return;
      
      const dayRow = enrichedSectionData[rowIndex];
      if (!dayRow) return;
      
      const dateStr = weekDates[day];
      
      // Get actual sales from MetricData
      let actSales = 0;
      if (storeActualData && storeActualData[dateStr] && storeActualData[dateStr].netSales) {
        actSales = storeActualData[dateStr].netSales;
      } else {
        // Fallback to projected sales if no actual data
        const projSalesStr = dayRow.projSales || '';
        actSales = parseFloat(projSalesStr.replace(/[$,\s]/g, '')) || 0;
      }

      
      // Get base theoretical hours from areasData configuration
      let fohBaseTheoHrs = 0;
      let bohBaseTheoHrs = 0;
      
      if (baseTheoHours && baseTheoHours.foh && baseTheoHours.boh) {
        fohBaseTheoHrs = parseFloat(baseTheoHours.foh[day]) || 0;
        bohBaseTheoHrs = parseFloat(baseTheoHours.boh[day]) || 0;
      } else {
        console.warn(`⚠️ No base theoretical hours found for store "${storeName}", using dayRow fallback`);
        // Fallback to dayRow values if areasData is not available
        fohBaseTheoHrs = parseFloat(dayRow.theoFOH) || 0;
        bohBaseTheoHrs = parseFloat(dayRow.theoBOH) || 0;
      }
      

      // Calculate variable hours based on sales using store-specific SPLH targets
      const isWeekday = WEEKDAY_DAYS.includes(day);
      const theoConfig = getTheoHoursConfig(storeName);
      const fohVarHrs = actSales / (isWeekday ? theoConfig.foh.weekday : theoConfig.foh.weekend);
      const bohVarHrs = actSales / (isWeekday ? theoConfig.boh.weekday : theoConfig.boh.weekend);
      

      // Calculate adjusted theoretical hours
      const adjTheoFOH = fohBaseTheoHrs + fohVarHrs;
      const adjTheoBOH = bohBaseTheoHrs + bohVarHrs;

      

      // Update the row with calculated adjusted values
      dayRow.adjTheoFOH = parseFloat(adjTheoFOH.toFixed(2));
      dayRow.adjTheoBOH = parseFloat(adjTheoBOH.toFixed(2));
      
      // Accumulate weekly totals
      weekTotals.adjTheoFOH += adjTheoFOH;
      weekTotals.adjTheoBOH += adjTheoBOH;
    });
    
    // Update totals row (typically at index i + 8)
    const totalRowIndex = i + 8;
    if (totalRowIndex < enrichedSectionData.length && enrichedSectionData[totalRowIndex]) {
      enrichedSectionData[totalRowIndex].adjTheoFOH = parseFloat(weekTotals.adjTheoFOH.toFixed(2));
      enrichedSectionData[totalRowIndex].adjTheoBOH = parseFloat(weekTotals.adjTheoBOH.toFixed(2));
      
    }
  }
  
  return enrichedSectionData;
}

/**
 * Fetch actual sales data from MetricData DynamoDB table
 */
async function fetchActualData(dateRange, selectedArea) {

  try {
    const metricNames = {
      NET_SALES: 'netSales',
      CHECK_COUNT: 'checkCount'
    };
    
    
    const metricData = await fetchMetricDataByClientDateAndName(
      'grm', // clientId - hardcoded for now, could be passed as parameter
      dateRange.startDate,
      dateRange.endDate,
      metricNames,
      2000 // Increase batch size for larger date ranges
    );
    
    
    
    return metricData;
  } catch (error) {
    console.error('❌ Error fetching actual sales data:', {
      message: error.message,
      errorType: error.constructor.name,
      stack: error.stack
    });
    throw error;
  }
}

/**
 * Process and combine projection and actual data
 *
 * @param {Array} projectionData - R0003 projection data
 * @param {Array} actualData - MetricData actual sales
 * @param {string} selectedArea - Area filter
 * @param {Map} storeIdMap - Store name to ID mapping
 * @param {Object} selectedDateRange - Date range
 * @param {Array} areasData - Areas data with store thresholds
 * @param {Array} thresholds - Global threshold configuration
 */
async function processSalesPerformanceData(projectionData, actualData, selectedArea, storeIdMap, selectedDateRange, areasData, thresholds) {

  try {
    // Validate inputs
    if (!projectionData) {
      throw new Error('projectionData is null or undefined');
    }
    if (!Array.isArray(projectionData)) {
      throw new Error('projectionData is not an array: ' + typeof projectionData);
    }
    if (!actualData) {
      console.warn('⚠️ actualData is null/undefined - will process with empty actual data');
    }

    // Group actual data by store and date
    const actualDataByStore = groupActualDataByStore(actualData || []);

    // Now calculate adjusted theoretical hours using the actual sales data
    console.log('🧮 Calculating adjusted theoretical hours with MetricData...');
    const enrichedProjectionData = calculateAdjustedTheoreticalHoursWithMetricData(
      projectionData, 
      actualDataByStore, 
      storeIdMap,
      areasData
    );
    
    // Extract stores from enriched projection data
    const storesWithData = [];
    
    // Process each area in enriched projection data
    for (let i = 0; i < enrichedProjectionData.length; i++) {
      const areaData = enrichedProjectionData[i];
      
      // Skip area filtering if selectedArea is null (which means show all areas/Brand view)
      if (selectedArea && areaData.area !== selectedArea) {
        continue; // Skip areas not matching selection
      }
      
      // Process stores within each area
      const areaStores = processAreaStoresMultiWeek(areaData, actualDataByStore, storeIdMap, selectedDateRange, areasData, thresholds);

      storesWithData.push(...areaStores);
    }
    
    
    const result = {
      restaurants: storesWithData,
      selectedArea,
      dataReady: true
    };
    
    return result;
    
  } catch (error) {
    console.error('❌ Error in processSalesPerformanceData:', {
      message: error.message,
      errorType: error.constructor.name,
      stack: error.stack
    });
    throw error;
  }
}

/**
 * Group actual metric data by store and date
 */
function groupActualDataByStore(actualData) {

  if (!actualData || !Array.isArray(actualData)) {
    console.warn('⚠️ actualData is not a valid array, returning empty object');
    return {};
  }

  const grouped = {};
  
  actualData.forEach((item, index) => {
    if (!item) {
      console.warn(`⚠️ Null item at index ${index}`);
      return;
    }

    const storeId = item.storeId;
    const date = item.date;
    const metricName = item.metricName;
    const numberValue = item.numberValue || 0;
    const dayPart = item.dayPart || 'unknown';
    const diningOption = item.diningOption || 'unknown';
    
    if (!storeId || !date || !metricName) {
      console.warn(`⚠️ Missing required fields in item ${index}:`, {
        storeId: !!storeId,
        date: !!date,
        metricName: !!metricName,
        item
      });
      return;
    }
    
    
    if (!grouped[storeId]) {
      grouped[storeId] = {};
    }
    
    if (!grouped[storeId][date]) {
      grouped[storeId][date] = { netSales: 0, checkCount: 0 };
    }
    
    // Aggregate across all day parts and dining options for the date
    grouped[storeId][date][metricName] += numberValue;
  });
  
  
  return grouped;
}

/**
 * Process stores within an area for multi-week date ranges
 *
 * @param {Object} areaData - Area data with week maps
 * @param {Object} actualDataByStore - Actual sales data grouped by store
 * @param {Map} storeIdMap - Store name to ID mapping
 * @param {Object} selectedDateRange - Date range
 * @param {Array} areasData - Areas data with store thresholds
 * @param {Array} thresholds - Global threshold configuration
 */
function processAreaStoresMultiWeek(areaData, actualDataByStore, storeIdMap, selectedDateRange, areasData, thresholds) {

  const stores = [];
  const NUM_DATA_ROWS = 11; // From R0003 structure
  
  if (!areaData.weekDataMap || areaData.weekDataMap.size === 0) {
    console.error('❌ No week data for area:', areaData?.area);
    return stores;
  }
  
  // Get unique store names from all weeks
  const storeNames = new Set();
  for (const [weekStart, weekData] of areaData.weekDataMap) {
    for (let i = 0; i < weekData.length; i += NUM_DATA_ROWS) {
      if (weekData[i] && weekData[i].day) {
        storeNames.add(weekData[i].day);
      }
    }
  }
  
  
  // Process each store
  for (const storeName of storeNames) {
    
    // Aggregate projection data across all weeks for this store
    let totalProjSales = 0;
    let totalProjCheckCount = 0;
    let daysProcessed = 0;
    
    for (const [weekStart, weekData] of areaData.weekDataMap) {
      // Find this store in the week data
      for (let i = 0; i < weekData.length - NUM_DATA_ROWS; i += NUM_DATA_ROWS) {
        if (weekData[i].day === storeName) {
          // Found the store, aggregate projection data for this week
          const projectionAggregation = aggregateProjectionDataForDateRange(
            weekData, 
            i, // storeStartIndex
            weekStart, 
            selectedDateRange
          );
          
          totalProjSales += projectionAggregation.projSales;
          totalProjCheckCount += projectionAggregation.projCheckCount;
          daysProcessed += projectionAggregation.daysIncluded.length;
          
          
          break; // Found this store in this week, move to next week
        }
      }
    }
    
    
    // Find corresponding actual data
    const storeId = getStoreIdFromMap(storeName, storeIdMap);
    
    let actualStoreData;
    if (storeId && actualDataByStore && actualDataByStore[storeId]) {
      actualStoreData = actualDataByStore[storeId];
    } else {
      actualStoreData = {};
    }
    
    const { actSales, actCheckCount } = aggregateActualData(actualStoreData);

    // Calculate all metrics with threshold adjustments
    const salesPerformance = calculateSalesMetrics({
      storeName,
      projSales: totalProjSales,
      actSales,
      projCheckCount: totalProjCheckCount,
      actCheckCount,
      areaName: areaData.area,
      storeId,
      areasData,
      thresholds
    });

    stores.push(salesPerformance);
  }
  
  // Sort stores alphabetically by store name
  stores.sort((a, b) => a.store.localeCompare(b.store));
  
  return stores;
}

/**
 * Aggregate projection data for specific date range from R0003 weekly data
 */
function aggregateProjectionDataForDateRange(sectionData, storeStartIndex, weekStartDate, selectedDateRange) {

  const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const weekStart = parseISO(weekStartDate);
  
  // Create date mapping: Mon = weekStart, Tue = weekStart+1, etc.
  const dayToDateMap = {};
  DAYS_OF_WEEK.forEach((day, index) => {
    const dayDate = addDays(weekStart, index);
    dayToDateMap[day] = format(dayDate, 'yyyy-MM-dd');
  });
  

  // Define date range interval for filtering
  const startDate = parseISO(selectedDateRange.startDate);
  const endDate = parseISO(selectedDateRange.endDate);
  const dateInterval = { start: startDate, end: endDate };

  let totalProjSales = 0;
  let totalProjCheckCount = 0;
  let daysIncluded = [];

  // Process daily rows (rows 1-7 after store name row)
  DAYS_OF_WEEK.forEach((day, dayIndex) => {
    const rowIndex = storeStartIndex + dayIndex + 1; // +1 to skip store name row
    const dayRow = sectionData[rowIndex];
    
    if (!dayRow) {
      console.warn(`⚠️ Missing day row for ${day} at index ${rowIndex}`);
      return;
    }

    const dayDate = dayToDateMap[day];
    const dayDateObj = parseISO(dayDate);
    
    // Check if this day falls within the selected date range
    if (isWithinInterval(dayDateObj, dateInterval)) {
      const rawProjSales = dayRow.projSales || '';
      const rawProjCheckCount = dayRow.projCheckCount || '';
      
      const dayProjSales = parseFloat(rawProjSales.replace(/[$,\s]/g, '')) || 0;
      const dayProjCheckCount = parseFloat(rawProjCheckCount.replace(/[,\s]/g, '')) || 0;
      
      totalProjSales += dayProjSales;
      totalProjCheckCount += dayProjCheckCount;
      
      daysIncluded.push({
        day,
        date: dayDate,
        projSales: dayProjSales,
        projCheckCount: dayProjCheckCount
      });
      
    }
  });


  return {
    projSales: totalProjSales,
    projCheckCount: totalProjCheckCount,
    daysIncluded
  };
}

/**
 * Get base theoretical hours for a store from areasData
 */
function getBaseTheoreticalHours(storeName, areasData) {
  if (!areasData || !Array.isArray(areasData)) {
    console.warn('⚠️ areasData is not available or not an array');
    return null;
  }
  
  // Search through all areas and their stores
  for (const area of areasData) {
    if (!area.stores || !Array.isArray(area.stores)) continue;
    
    for (const store of area.stores) {
      if (store.shortName === storeName || store.storeName === storeName) {
        
        return {
          foh: store.fohBaseTheoHrs,
          boh: store.bohBaseTheoHrs
        };
      }
    }
  }
  
  console.warn(`⚠️ No base theoretical hours found for store "${storeName}" in areasData`);
  return null;
}

/**
 * Get store ID from store name using storeIdMap
 */
function getStoreIdFromMap(storeName, storeIdMap) {
  if (!storeIdMap || !storeIdMap.has) {
    return null;
  }
  
  // Try exact match first
  if (storeIdMap.has(storeName)) {
    return storeIdMap.get(storeName);
  }
  
  // Try case-insensitive match
  for (const [mapStoreName, storeId] of storeIdMap.entries()) {
    if (mapStoreName.toLowerCase() === storeName.toLowerCase()) {
      return storeId;
    }
  }
  
  return null;
}

/**
 * Aggregate actual data across date range
 */
function aggregateActualData(storeData) {

  // Handle null/undefined storeData
  if (!storeData || typeof storeData !== 'object') {
    console.warn('⚠️ storeData is null/undefined/invalid, returning zero values');
    return { actSales: 0, actCheckCount: 0 };
  }

  let actSales = 0;
  let actCheckCount = 0;
  
  try {
    const dayDataArray = Object.values(storeData);

    dayDataArray.forEach((dayData, index) => {
      if (!dayData || typeof dayData !== 'object') {
        console.warn(`⚠️ Invalid day data at index ${index}:`, dayData);
        return;
      }

      // dayData represents metrics for one date: { netSales: X, checkCount: Y }
      const netSales = dayData.netSales || 0;
      const checkCount = dayData.checkCount || 0;
      
      actSales += netSales;
      actCheckCount += checkCount;
      
    });
    
    return { actSales, actCheckCount };
    
  } catch (error) {
    console.error('❌ Error in aggregateActualData:', error);
    return { actSales: 0, actCheckCount: 0 };
  }
}

/**
 * Calculate all sales performance metrics based on requirements
 * Applies average check threshold adjustments to projAvgCheck baseline
 *
 * @param {Object} params - Calculation parameters
 * @param {string} params.storeName - Store display name
 * @param {number} params.projSales - Projected sales
 * @param {number} params.actSales - Actual sales
 * @param {number} params.projCheckCount - Projected check count
 * @param {number} params.actCheckCount - Actual check count
 * @param {string} params.areaName - Area name
 * @param {string} params.storeId - Store ID (storeRecordId from MetricData)
 * @param {Array} params.areasData - Areas data with store thresholds
 * @param {Array} params.thresholds - Global threshold configuration
 */
function calculateSalesMetrics({ storeName, projSales, actSales, projCheckCount, actCheckCount, areaName, storeId, areasData, thresholds }) {
  // Variance calculations
  const salesVariance = actSales - projSales;
  const salesVariancePercent = projSales > 0 ? (salesVariance / projSales) * 100 : 0;

  const checkCountVariance = actCheckCount - projCheckCount;

  // Average Check calculations
  const actualAvgCheck = actCheckCount > 0 ? actSales / actCheckCount : 0;
  let projAvgCheck = projCheckCount > 0 ? projSales / projCheckCount : 0;

  // CRITICAL: Apply avgCheckHigh threshold adjustment to projAvgCheck baseline
  // The projAvgCheck from database is unadjusted (saved unadjusted in R0003.1)
  // We need to adjust it here for variance and opportunity calculations
  let adjustedProjAvgCheck = projAvgCheck;
  let avgCheckAdjustment = 0;
  let hasThresholdAssignment = false; // Track if store has an explicit threshold assignment

  if (areasData && thresholds) {
    // IMPORTANT: Match stores by shortName (not storeRecordId)
    // areasData uses shortName as the primary identifier
    // This matches the pattern used in R0003.2 SPLH Report
    let storeFound = false;

    // Find store's threshold assignment from areasData by matching shortName
    for (const area of areasData) {
      if (!area.stores) continue;
      for (const store of area.stores) {
        // Match by shortName (the store display name)
        if (store.shortName === storeName) {
          storeFound = true;
          const avgCheckThresholdId = store.thresholds?.avgCheck;

          const highThreshold = getAvgCheckHighThreshold(
            storeId, // Can pass storeId or null - not used when passing thresholdId directly
            thresholds,
            avgCheckThresholdId
          );

          avgCheckAdjustment = highThreshold;
          adjustedProjAvgCheck = applyAvgCheckAdjustment(projAvgCheck, highThreshold);

          // Store whether this store has a threshold assignment (for area rollup calculation)
          hasThresholdAssignment = !!avgCheckThresholdId;
          break;
        }
      }
      if (storeFound) break;
    }
  }

  // Calculate variance using ADJUSTED projAvgCheck baseline
  const avgCheckVariance = actualAvgCheck - adjustedProjAvgCheck;

  // Round the average check variance to 2 decimal places for consistent calculations
  const avgCheckVarianceRounded = parseFloat(avgCheckVariance.toFixed(2));

  // Opportunity calculation (only if negative average check variance)
  // Uses adjusted baseline - if actual < adjusted projection, there's missed opportunity
  let opportunity = 0;
  if (avgCheckVarianceRounded < 0) {
    // Use the rounded variance to match what's displayed in the table
    const missedOpportunity = Math.abs(avgCheckVarianceRounded) * actCheckCount;
    opportunity = missedOpportunity;
  }
  
  const result = {
    store: storeName,
    areaName,
    storeId,

    // Core metrics
    projection: projSales,
    actual: actSales,
    checkCount: actCheckCount,
    projCheckCount,

    // Calculated metrics using ADJUSTED projAvgCheck baseline
    variance: salesVariance,
    variancePercent: salesVariancePercent,
    checkCountVariance,
    avgCheck: actualAvgCheck,
    projAvgCheck: adjustedProjAvgCheck, // Return ADJUSTED value for display
    avgCheckVariance: avgCheckVarianceRounded, // Already calculated using adjusted baseline
    opportunity, // Already calculated using adjusted baseline

    // Additional data for UI
    projSales, // For backward compatibility with existing table
    actSales,
    actCheckCount,

    // Internal data for debugging/transparency
    _unadjustedProjAvgCheck: projAvgCheck, // Original unadjusted value
    _avgCheckAdjustment: avgCheckAdjustment, // Adjustment applied in cents
    _hasThresholdAssignment: hasThresholdAssignment // Whether store has explicit threshold assignment
  };

  return result;
}